import { useEffect, useState } from "react";

import { organizations, type Organization } from "../lib/api.js";

/**
 * Switches which organization the session is looking at.
 *
 * Switching does a full page load rather than re-rendering in place. That is
 * deliberate: every list on screen was fetched for the previous organization,
 * and several of them refresh on their own timers, so an in-place switch shows
 * one organization's devices under another's name until each happens to tick.
 * A brief reload is a much smaller cost than a screen that is quietly lying
 * about whose equipment it is showing.
 *
 * Rendered as plain text when there is only one, so a single-organization
 * account is not given a control that does nothing.
 */
export function OrgSwitcher({ activeName }: { activeName: string }) {
  const [list, setList] = useState<Organization[] | null>(null);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    organizations
      .list()
      .then((r) => setList(r.organizations))
      .catch(() => setList(null));
  }, []);

  if (!list || list.length <= 1) {
    return <span style={{ color: "var(--text-secondary)" }}>{activeName.toUpperCase()}</span>;
  }

  const switchTo = async (id: string) => {
    setSwitching(true);
    try {
      await organizations.activate(id);
      window.location.assign("/");
    } catch {
      setSwitching(false);
    }
  };

  return (
    <select
      value={list.find((o) => o.name === activeName)?.id ?? ""}
      disabled={switching}
      onChange={(e) => void switchTo(e.target.value)}
      aria-label="Active organization"
      style={{
        background: "var(--surface-raised)",
        color: "var(--text-secondary)",
        border: "1px solid var(--border)",
        padding: "4px 6px",
        fontSize: 12,
        letterSpacing: "0.08em",
        maxWidth: 220,
      }}
    >
      {list.map((org) => (
        <option key={org.id} value={org.id}>
          {org.name.toUpperCase()}
        </option>
      ))}
    </select>
  );
}
